import { AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'
import { createContext, useContext } from 'react'

import { StoreInstances } from '../../../../src/store/StoreInstancesContainer'

import { LineType, NonCachedSearchType } from 'interfaces'
import { embeddedConfigManager } from 'src/api/embedded/EmbeddedConfigManager'
import { EmbeddedCommunicationsManager } from 'src/api/embedded/EmbeddedCommunicationsManager'

import { stockOptionsCases } from 'src/features/filterResults/components/stockOptionsCases'

import ListServiceProvider from 'src/services/ListServiceProvider'
import { IStoreCard } from 'src/services/MultiSellerServicesProvider'
import PartServiceProvider from 'src/services/PartServiceProvider'

import {
  VerifyStockedPartsRequest,
  VerifyStockedPartsResponse,
} from 'src/store/lists/interfaces'

import { AvailableStores, ProductLocationModel, ProductModel } from 'interfaces'
import { PartsCatalogSelections } from '../../../../src/store/models/PartsCatalogModels'
import { PartType } from '../../../../src/store/models/PartsCatalogStoreModels'
import {
  Direction,
  Filter,
  Filters,
  InterchangePartsBinRequest,
  InterchangeRequest,
  LinesManufacturer,
  MultiPartInquiryModel,
  SearchFilterModel,
  SearchResultsModel,
  SearchResultsResponse,
  SortOrder,
  UpdatedPartInfoDetails,
} from '../../../../src/store/models/SearchModels'
import { productsMatch } from 'cart'
import {
  clearPersistedAppliedFilters,
  persistAppliedFilters,
  readPersistedAppliedFilters,
} from './persistedAppliedFilters'

import { PartsCatalog } from '../../../../src/store/partsCatalog/PartsCatalog'
import { UserAttributeKey } from '../../../../src/store/user/interfaces'

import { monitorUpdates } from 'common'
import { TrackingManager } from 'src/config/analytics/tracking'
import AttributesServiceProvider from '../../../../src/services/AttributesServiceProvider'
import { UuidType } from '../../../../src/services/attributes/AttributesModel'
import { getSortOptions } from '../utils/GetSortOptions'
import {
  IEnableResortItems,
  ProductCatalogInfo,
  SaytRecommendation,
} from '../interfaces'
import {
  ClearSAYT,
  DEFAULT_SORT_ORDER,
  FilterGroupName,
  LOAD_PER_REQUEST,
  LineTypes,
  RETRY_SEARCH_LIMIT,
  StockOptions,
  VEHICLE_SYSTEM,
} from '../constants'
import {
  fetchMissedImages,
  mergeMissedImages,
} from '../utils/handleMissedImages'
import { hawkSearchTracking } from 'src/helpers/HawkSearchTracking'

export const ResultsStoreContext = createContext<ResultStore | undefined>(
  undefined
)

export const STORAGE_SEARCH_STATE_KEY = 'search-state'

const partDomain = (): string =>
  StoreInstances?.userStore?.getUserAttribute(UserAttributeKey.partDomain) || ''

export class ResultStore {
  diagrams: SearchResultsModel | undefined = undefined

  /**
   * Available filters for the current set of results.
   */
  currentAvailableFilters: Array<SearchFilterModel> = []

  /**
   * The filter type that is selected first.
   */
  firstSelectedFilterType: FilterGroupName | undefined = undefined

  searchResults: SearchResultsModel = { filters: [] }

  enableResortItems: IEnableResortItems | null = null

  filters: Filters = []

  defaultStockFilterValue = ''

  sortOrder: SortOrder = []

  availableStores: AvailableStores = {}

  gfxSelections: Array<string>

  gfxLookupMade = false

  isLoading = false

  hasMoreResults = false

  lookupTitle: string | undefined = undefined

  searchError = ''

  showInterchangeDisclaimers = false

  lastNonCachedSearch: NonCachedSearchType = NonCachedSearchType.none

  partsBinId: string

  /*
    Note:
      lastTextSearched
      lastInterchangeTextSearched
      We could have used a single variable but in the future we'll have to display a history
      of sayt searches, so the lastTextSearched will become a list
  */
  lastTextSearched: string

  lastInterchangeTextSearched: string

  // new select lines from manufacturers

  linesManufacturersData: LinesManufacturer[] = []

  linesManufacturersDataLoading = false

  selectedLinesManufacturers: string[] = []

  showAllLinesForOrangeParts = false

  selectedCatalogDiagram: ProductModel = {
    lineCode: '',
    location: [],
    partNumber: '',
    eligibleBundles: [],
  }

  showAddAlltoCart = false

  multiPartInquiryResults: MultiPartInquiryModel[] = []

  cartSelectorModal = false

  retrySearchCount = 0

  paginationOffset = 0

  searchResultPageScrollPosition = 0

  private _saytRecommendations: SaytRecommendation[]

  cgts: PartsCatalogSelections = []

  private _interchangeRequestData: InterchangeRequest | null = null

  public set saytRecommendations(recommendations: SaytRecommendation[]) {
    this._saytRecommendations = recommendations
  }

  public get saytRecommendations(): SaytRecommendation[] {
    return this._saytRecommendations
  }

  updatedPartInfoDetails: Array<UpdatedPartInfoDetails> = []

  sellerDetails: IStoreCard | null = null

  constructor() {
    makeAutoObservable(this)
  }

  public setDefaultStockFilterValue = (value: string): void => {
    this.defaultStockFilterValue = value
  }

  public setEnableResortItems = (): void => {
    this.enableResortItems = null
  }

  setLookupTitle = (title: string): void => {
    this.lookupTitle = title
  }

  setSearchResults = (results: SearchResultsResponse): void => {
    this.searchResults = results
  }

  setFilters = (filters: Filters): void => {
    this.filters = filters
    this.persistCurrentAppliedFilters()
    if (filters[0]?.items[0]?.valueOf() !== LineTypes.ALL_LINES) {
      this.clearResults(ClearSAYT.NO)
    }
  }

  setAvailableStores = (availableStores: AvailableStores): void => {
    this.availableStores = availableStores
  }

  setSortOrder = (sortOrder: SortOrder, reload = true): void => {
    this.saveSortOrderToCloud(sortOrder)
    this.sortOrder = sortOrder
    if (!reload) {
      return
    }
    if (this.lastNonCachedSearch === NonCachedSearchType.searchByInterchange) {
      StoreInstances.uiStore.showApplySortMessage = false
      this.applyInterchangeSearchSort()
      return
    }
    if (!this.gfxLookupMade) {
      this.loadNextPage(true)
    }
    this.setHasMoreResults(true)
  }

  resetFiltersForNewSearch = (): void => {
    this.filters = []
    this.currentAvailableFilters = []
    this.selectedLinesManufacturers = []
    this.firstSelectedFilterType = undefined
    clearPersistedAppliedFilters()
  }

  setFirstSelectedFilterType = (
    firstSelectedFilterType: FilterGroupName | undefined
  ): void => {
    this.firstSelectedFilterType = firstSelectedFilterType
    this.persistCurrentAppliedFilters()
  }

  private readonly resolveDisplayFilters = (
    apiFilters?: SearchFilterModel[]
  ): SearchFilterModel[] => {
    const api = apiFilters ?? []
    const existing = this.searchResults?.filters ?? []
    if (api.length > 0) {
      return api
    }
    return existing
  }

  private readonly updateResortItems = (
    results: SearchResultsResponse
  ): void => {
    if (!this.searchResults.isSnwFinished && results.isSnwFinished) {
      if (!this.enableResortItems?.enable) {
        const lastPart =
          this.searchResults.parts[this.searchResults.parts.length - 1]
        if (!lastPart) {
          this.enableResortItems = null
          return
        }
        this.enableResortItems = {
          enable: true,
          partNumber: lastPart.partNumber,
          lineCode: lastPart.lineCode,
          description: lastPart.description,
        }
      } else {
        this.enableResortItems = null
      }
    }
  }

  private readonly handleEmptyResults = (
    results: SearchResultsResponse
  ): void => {
    this.retrySearchCount++
    const displayFilters = this.resolveDisplayFilters(results.filters)
    if (displayFilters.length > 0) {
      this.searchResults.filters = displayFilters
    }
    if (this.retrySearchCount >= RETRY_SEARCH_LIMIT) {
      this.setHasMoreResults(false)
      this.retrySearchCount = 0
    }
  }

  private readonly mergeParts = (
    currentParts: ProductModel[],
    newParts: ProductModel[],
    append: boolean
  ): ProductModel[] => {
    if (!append) return [...newParts]
    return [
      ...currentParts,
      ...newParts.filter(
        (np) => !currentParts.some((cp) => productsMatch(cp, np))
      ),
    ]
  }

  private readonly processSearchResults = async (
    results: SearchResultsResponse,
    append = false
  ): Promise<void> => {
    const currentParts = this.searchResults?.parts ?? []

    this.updateResortItems(results)

    const newParts = embeddedConfigManager.isLocalInventoryEnabled
      ? await EmbeddedCommunicationsManager.getLocalInventory(results)
      : results.parts || []

    if (!newParts?.length) {
      this.handleEmptyResults(results)
      return
    }

    const totalResults = results.filteredCount ?? results.totalCount ?? 0
    if (totalResults > 10) {
      this.setHasMoreResults(true)
    }

    const mergedParts = this.mergeParts(currentParts, newParts, append)

    // Stop pagination if appending produced no new unique parts
    if (append && mergedParts.length === currentParts.length) {
      this.setHasMoreResults(false)
    }

    this.setSearchResults({
      ...results,
      parts: mergedParts,
      filters: this.resolveDisplayFilters(results.filters),
    })

    this.trackSearch(newParts)

    this.addStockInformationToParts(newParts)
    fetchMissedImages(this.searchResults).then((partImages) => {
      this.searchResults = mergeMissedImages(this.searchResults, partImages)
    })
  }

  private readonly addStockInformationToParts = async (
    newParts: ProductModel[]
  ): Promise<void> => {
    const allParts = newParts.flatMap((part) => [
      part,
      ...(part.alternateParts || []),
      ...(part.replacementParts || []),
    ])

    const stockRequest: VerifyStockedPartsRequest[] = allParts
      .filter(
        ({ lineCode, partNumber, type }) =>
          lineCode && partNumber && type === LineType.part
      )
      .map(({ lineCode, partNumber }) => ({
        lineCode,
        partNumber,
      })) as VerifyStockedPartsResponse[]

    if (stockRequest.length < 1) return
    const newPartStockInformation =
      await ListServiceProvider.verifyStockedParts(stockRequest)

    if (!newPartStockInformation || newPartStockInformation.length < 1) return

    this.searchResults.parts
      ?.flatMap((p) => [
        p,
        ...(p.alternateParts || []),
        ...(p.replacementParts || []),
      ])
      ?.forEach((part) => {
        const partFound = newPartStockInformation.find(
          (stockPart: VerifyStockedPartsResponse) =>
            part.partNumber.toUpperCase() ===
              stockPart.partNumber.toUpperCase() &&
            part.lineCode.toUpperCase() === stockPart.lineCode.toUpperCase()
        )
        if (partFound) part.isStocked = partFound.isStocked
      })
  }

  searchByPartBin = async (
    startOffset?: number,
    filterName?: string,
    initial = false,
    resorted = false
  ): Promise<void> => {
    const start = startOffset ?? this.paginationOffset
    if (!this.partsBinId) {
      return
    }
    this.paginationOffset = start + LOAD_PER_REQUEST
    try {
      const reqData = this.preparePartsBinRequestData(start)
      this.setIsLoading(true)
      let queryOverrides
      if (this.lastNonCachedSearch === NonCachedSearchType.searchByText) {
        queryOverrides = {
          catalogId: '7',
          covListId: partDomain(),
          partDomain: partDomain(),
        }
      }
      if (resorted) {
        queryOverrides = {
          ...queryOverrides,
          applyReSort: 'true',
        }
      } else {
        queryOverrides = {
          ...queryOverrides,
          applyReSort: 'false',
        }
      }
      const results = await PartServiceProvider.getPartsBinResults(
        reqData.partsBinId,
        reqData,
        queryOverrides
      )
      // Reusing the initial filters if users selects otherthen lines filter(we are updateing filters only if user selects lines).
      if (!initial && results && filterName !== FilterGroupName.LINES) {
        this.currentAvailableFilters = results.filters || []
        const existingFilters = this.searchResults?.filters ?? []
        if (this.filters.length > 0 && existingFilters.length > 0) {
          results.filters = existingFilters
        }
      }
      await this.processSearchResults(results, start > 0)
      this.setIsLoading(false)
    } catch (e) {
      const statusCode = (e as AxiosError)?.response?.status
      if (statusCode === 404) {
        StoreInstances.searchStore.executeLastNonCachedSearch(start)
      } else {
        this.setIsLoading(false)
      }
    }
  }

  searchByGfxPartBin = (startOffset?: number): void => {
    const start = startOffset ?? this.searchResults?.parts?.length ?? 0
    if (!this.partsBinId) {
      return
    }
    this.clearResults()

    try {
      const reqData = this.prepareGfxPartsBinRequestData()
      this.executeSearch(
        () =>
          PartServiceProvider.getGfxPartsBinResults(this.partsBinId, reqData),
        start > 0
      )
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
      if ((e as any)?.response?.status === 404)
        StoreInstances.searchStore.executeLastNonCachedSearch(start)
    }
  }

  searchByInterchange = (): void => {
    const reqData = this._interchangeRequestData

    if (!reqData) {
      return
    }

    const apiCall = () => this.monitorPriceForInterchangeSearch(reqData)

    this.executeSearch(apiCall, false)
  }

  monitorPriceForInterchangeSearch = async (
    reqData: InterchangePartsBinRequest
  ): Promise<SearchResultsModel> => {
    type R = SearchResultsResponse

    const newReqData = {
      ...reqData,
      clientUTCOffset: StoreInstances.userStore.utcOffset,
    }
    const target = (): Promise<R> =>
      PartServiceProvider.getPriceForInterchangeParts(
        this.partsBinId,
        newReqData
      )

    const passCheck = (resp: R) => resp.isSnwFinished || false

    /*
    MPV3-5810 - temporary disabled code - Nov 13th 2024
    StoreInstances.uiStore.showMonitoringPricesMessage = true
    */

    const resp = await monitorUpdates<R>({
      target,
      passCheck,
    })

    /*
    MPV3-5810 - temporary disabled code - Nov 13th 2024
    StoreInstances.uiStore.showMonitoringPricesMessage = false
    StoreInstances.uiStore.showApplySortMessage = true
    */

    this.isLoading = false // MPV3-5810 temporary line - remove once the API issues are resolved.
    // this.searchResults.totalCount = resp.totalCount
    // this.searchResults.filteredCount = resp.filteredCount
    // this.searchResults.isSnwFinished = resp?.isSnwFinished
    // this.searchResults.parts = [] // Force mobx to update all elements
    // this.searchResults.parts = resp.parts

    if (resp) {
      this.addStockInformationToParts(resp.parts || [])
      return {
        ...resp,
        filters: this.searchResults.filters,
      }
    }
    return {
      filteredCount: 0,
      filters: [],
      isSnwFinished: true,
      parts: [],
      partsBinId: '',
      totalCount: 0,
    }
  }

  applyInterchangeSearchSort = async (): Promise<void> => {
    if (!this._interchangeRequestData) {
      return
    }
    this.setIsLoading(true)
    const sortOrder = this.sortOrder
    const stock = this.getStockFilter()

    const manufacturers =
      this.getAppliedFiltersByGroupName(FilterGroupName.BRANDS) ??
      this.getAvailableFiltersByGroupName(FilterGroupName.BRANDS)

    const partTypes =
      this.getAppliedFiltersByGroupName(FilterGroupName.PART_TYPE) ??
      this.getAvailableFiltersByGroupName(FilterGroupName.PART_TYPE)
    this._interchangeRequestData = {
      ...this._interchangeRequestData,
      manufacturers,
      partTypes,
      sortOrder,
      stock,
      clientUTCOffset: StoreInstances.userStore.utcOffset,
    }
    const reqData = this._interchangeRequestData
    const resp = await PartServiceProvider.getPriceForInterchangeParts(
      this.partsBinId,
      reqData
    )
    this.processSearchResults(
      { ...resp, filters: this.searchResults.filters },
      false
    )
    this.setIsLoading(false)
  }

  private getStockFilter = (): string => {
    const getItems = (g: string) =>
      this.filters?.filter((f) => f.groupName === g).flatMap((f) => f.items)
    const stock = getItems('Stock')?.[0]

    if (stock) return stock

    return this.getDefaultStockFilterOption(
      StoreInstances?.userStore?.preferences?.display_filtering,
      StoreInstances?.userStore?.preferences?.display_userFiltering
    )
  }

  getAppliedFiltersByGroupName = (g: string): string[] => {
    const filters = this.filters
      ?.filter((f) => f.groupName === g)
      ?.flatMap((f) => f.items)

    return filters.length > 0 ? filters : []
  }

  getAvailableFiltersByGroupName = (g: string): string[] => {
    const filters = (this.searchResults?.filters || [])
      .filter((f) => f.groupName === g)
      .flatMap((f) => f.items)

    return filters.length > 0 ? filters : []
  }

  applyInterchangeSearchFilters = async (): Promise<void> => {
    StoreInstances.uiStore.showApplySortMessage = false
    this.isLoading = true
    const sortOrder = this.sortOrder

    const manufacturers =
      this.getAppliedFiltersByGroupName(FilterGroupName.BRANDS) ??
      this.getAvailableFiltersByGroupName(FilterGroupName.BRANDS)

    const partTypes =
      this.getAppliedFiltersByGroupName(FilterGroupName.PART_TYPE) ??
      this.getAvailableFiltersByGroupName(FilterGroupName.PART_TYPE)

    const stock = this.getStockFilter()

    const reqData = {
      manufacturers,
      partTypes,
      sortOrder,
      stock,
      clientUTCOffset: StoreInstances.userStore.utcOffset,
    }

    const resp = await PartServiceProvider.getPriceForInterchangeParts(
      this.partsBinId,
      reqData
    )
    const filters =
      this.filters.length > 0
        ? this.searchResults.filters
        : (resp.filters ?? this.searchResults.filters)
    this.processSearchResults({ ...resp, filters }, false)
    this.setIsLoading(false)
  }

  searchDiagramsByPartsBinId = async (partsBinId: string): Promise<void> => {
    try {
      this.diagrams = await PartServiceProvider.getDiagramsSearch(partsBinId)
    } catch (_e) {
      //
    }
  }

  private executeSearch = (
    apiCall: () => Promise<SearchResultsResponse>,
    appendResults: boolean
  ) => {
    this.setIsLoading(true)
    this.setHasMoreResults(true)
    StoreInstances.uiStore.showApplySortMessage = false
    apiCall()
      .then((results) => {
        if (
          results?.parts?.length === 0 &&
          (results?.filters || []).length > 0
        ) {
          this.searchResults.filters = results.filters
        }
        if (!appendResults) {
          //resetting qtyUpdatedPartDetails to initial value.
          this.updatedPartInfoDetails = []
          this.setSearchResultPageScrollPosition(0)
        }
        this.currentAvailableFilters = results.filters || []
        // when status code returns 204 and results are 0, resetting results and filters
        if (results.totalCount === undefined) {
          this.searchResults = {}
          this.setIsLoading(false) // MPV3-5810 - temporary code - Nov 13th 2024
        }
        return this.processSearchResults(results, appendResults)
      })
      .catch((e) => {
        this.searchResults = {}
        this.setSearchError(e.message)
      })
      .finally(() => {
        /*
          MPV3-5810 - temporary code - Nov 13th 2024
          Once the API is ready, run this.setIsLoading(false) regardles of the search type
        */
        if (
          this.lastNonCachedSearch !== NonCachedSearchType.searchByInterchange
        ) {
          this.setIsLoading(false)
        }
      })
  }

  loadNextPage = async (force = false): Promise<void> => {
    if (this.isLoading) return

    if (this.gfxLookupMade) {
      await this.loadNextGfxPage(force)
    } else {
      await this.loadNextStandardPage(force)
    }
  }

  private readonly loadNextGfxPage = async (force: boolean): Promise<void> => {
    const reqData = this.prepareGfxPartsBinRequestData()
    const currentLength = this.searchResults?.parts?.length ?? 0
    const { totalCount, filteredCount } = this.searchResults
    const totalResults = filteredCount ?? totalCount ?? 0

    if (currentLength >= totalResults && !force) {
      this.setHasMoreResults(false)
      return
    }

    reqData.start = force ? 0 : currentLength
    if (this.sortOrder.length > 0) {
      reqData.sortOrder = this.sortOrder.map((item) => ({
        property: item.property,
        dir: item.dir,
      }))
    }

    let results
    try {
      this.setIsLoading(true)
      results = await PartServiceProvider.getGfxPartsBinResults(
        reqData.partsBinId,
        reqData
      )
    } finally {
      this.setIsLoading(false)
    }

    this.currentAvailableFilters = results.filters || []
    const currentParts = this.searchResults?.parts ?? []
    const newParts = results.parts ?? []
    this.setSearchResults({
      ...results,
      parts: force ? [...newParts] : [...currentParts, ...newParts],
    })
  }

  private readonly loadNextStandardPage = async (
    force: boolean
  ): Promise<void> => {
    const { totalCount, filteredCount } = this.searchResults
    const totalResults = filteredCount ?? totalCount ?? 0

    if (this.paginationOffset >= totalResults && !force) {
      this.setHasMoreResults(false)
      return
    }
    if (force) {
      this.paginationOffset = 0
    }
    await this.searchByPartBin(this.paginationOffset)
  }

  public setIsLoading = (isLoading: boolean): void => {
    this.isLoading = isLoading
  }

  public setHasMoreResults = (hasMore: boolean): void => {
    this.hasMoreResults = hasMore
  }

  private setSearchError = (searchError: string) => {
    this.searchError = searchError
  }

  public clearResults = (clearSAYT: ClearSAYT = ClearSAYT.YES): void => {
    if (!this.searchResults) return

    this.searchResults.parts = []
    this.searchResults.totalCount = 0
    this.searchResults.filteredCount = 0
    this.searchResults.isSnwFinished = false
    this.paginationOffset = 0
    if (clearSAYT === ClearSAYT.YES) {
      this.saytRecommendations = []
    }
  }

  private preparePartsBinRequestData = (start: number) => {
    return {
      partsBinId: this.partsBinId,
      ...this.prepareCommonRequestData(start),
    }
  }

  private prepareGfxPartsBinRequestData = () => {
    const start = this.searchResults?.parts?.length ?? 0
    return {
      partsBinId: this.partsBinId,
      gfxPartTypes: this.gfxSelections,
      ...this.prepareCommonRequestData(start),
    }
  }

  // TODO: refactor this function to make it easier to read.
  public getDefaultStockFilterOption = (
    displayFiltering: string,
    displayUserFiltering: string
  ): string => {
    const displayUserFilteringValue = (displayUserFilteringOption: string) => {
      if (displayUserFilteringOption === '1') {
        return StockOptions.ONLY_PRICED
      }

      if (displayUserFilteringOption === '2') {
        return StockOptions.ONLY_STOCKED
      }
      return StockOptions.SHOW_ALL
    }

    const stockOptions = stockOptionsCases(displayFiltering)

    const findDefaultOption = stockOptions.items.find(
      (item) => item === displayUserFilteringValue(displayUserFiltering)
    )
    if (findDefaultOption) {
      this.setDefaultStockFilterValue(
        displayUserFilteringValue(displayUserFiltering)
      )
      return displayUserFilteringValue(displayUserFiltering)
    }
    this.setDefaultStockFilterValue(stockOptions.items[0])
    return stockOptions?.items[0]
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public prepareCommonRequestData = (start: number) => {
    // Resetting the enableResortItems as user can come to search result page from YMME, Interchange, Search etc.
    if (!start) {
      this.setEnableResortItems()
    }
    const linesfilter = this.filters.find(
      (filter) => filter.groupName === FilterGroupName.LINES
    )
    const selectedLine = linesfilter && linesfilter.items[0]

    let lineLookupType = 1
    if (selectedLine === LineTypes.SECONDARY) {
      lineLookupType = 2
    }
    if (
      selectedLine === LineTypes.ALL_LINES &&
      this.selectedLinesManufacturers.length > 0
    ) {
      lineLookupType = 3
    }

    const defaultStockFilter = this.filters.find(
      (item) => item.groupName === FilterGroupName.STOCK
    )
      ? []
      : [
          {
            groupName: FilterGroupName.STOCK,
            items: [
              this.getDefaultStockFilterOption(
                StoreInstances?.userStore?.preferences?.display_filtering,
                StoreInstances?.userStore?.preferences?.display_userFiltering
              ),
            ],
          },
        ]
    return {
      start,
      limit: LOAD_PER_REQUEST,
      filters: [
        ...this.filters.filter(
          // Remove the lines group from the filters.
          (filter) => filter.groupName !== FilterGroupName.LINES
        ),
        ...defaultStockFilter,
      ],
      lineLookupType,
      manufacturers: this.selectedLinesManufacturers,
      sortOrder: this.sortOrder.map((item) => ({
        property: item.property,
        dir: item.dir,
      })),
      clientUTCOffset: StoreInstances.userStore.utcOffset,
    }
  }

  public restoreFiltersFromCache = (): void => {
    const sortOrderString = StoreInstances?.userStore?.getUserAttribute(
      UserAttributeKey.displaySortOrder
    )
    if (sortOrderString) {
      const sortOrderNestedList = sortOrderString.split(',').map((option) => {
        const innerOption = option.split('_')
        return [innerOption[0].trim(), innerOption[1].trim()]
      })
      const options = getSortOptions()
      const sortByThenBy: SortOrder = sortOrderNestedList
        .slice(0, 2)
        .map((list) => {
          let dir: Direction = list[1] === '0' ? 'asc' : 'desc'
          let property = list[0]
          if (property === 'MCL') {
            property = 'Recommended'
            dir = 'desc' // Because Recommended has no asc dir in the options above
          } else if (property === 'Availability') {
            dir = 'desc' // Because Availability has no asc dir in the options above
          } else if (property === 'PartNumber') {
            property = 'Part Number'
          } else if (property === 'Description') {
            property = 'Part Description'
          }
          const label = options.find(
            (option) => option.property === property && option.dir === dir
          ).label
          return { property, dir, label }
        })
      if (sortByThenBy.length > 0) this.setSortOrder(sortByThenBy, false)
    } else {
      this.setSortOrder(DEFAULT_SORT_ORDER, false)
    }
  }

  private trackSearch(parts: Array<ProductModel>) {
    const allParts = parts?.reduce((acc, p) => {
      const partsToAdd = [p]
      if (p.alternateParts) partsToAdd.push(...p.alternateParts)
      if (p.replacementParts) partsToAdd.push(...p.replacementParts)
      return [...acc, ...partsToAdd]
    }, [] as ProductModel[])

    const searchTerm =
      this.gaLastSearchTerm() || StoreInstances.searchStore.gaLastSearchTerm()
    const vehicleSystem =
      StoreInstances.searchStore.gfxAttributesAnswers.get(VEHICLE_SYSTEM)
    TrackingManager.trackSearchResults(
      allParts,
      this.lastNonCachedSearch,
      this.gaProductCatalogInfo,
      searchTerm,
      vehicleSystem,
      this.searchResults?.multiSeller,
      this.filters
    )
  }

  public gaLastSearchTerm = (): string => {
    if (
      this.lastNonCachedSearch === NonCachedSearchType.searchByText ||
      this.lastNonCachedSearch === NonCachedSearchType.searchBySaytCategories
    )
      return (
        this.lastTextSearched ?? StoreInstances.searchStore.lastTextSearched
      )

    if (this.lastNonCachedSearch === NonCachedSearchType.searchByInterchange)
      return (
        this.lastInterchangeTextSearched ??
        StoreInstances.searchStore.lastInterchangeTextSearched
      )

    return ''
  }

  // This method is to feed google analytics, don't use for something else
  public gaProductCatalogInfo = (product: ProductModel): ProductCatalogInfo => {
    const { selectedCategories, selectedGroups, selectedPartTypes } =
      this.lastPartsCatalogInstance()

    // A group can belong to multiple categories, but just getting the first one is fine for now
    const category = selectedCategories.find((c) =>
      c.children.find((g) => g.id === product.group?.id)
    )

    const group = selectedGroups.find((g) => g.id === product.group?.id)

    const partType = selectedPartTypes.find(
      (pt) => pt.id.toString() === product.allianceterminologyId.toString()
    )

    return {
      category: category?.value,
      group: group?.value,
      partType: partType?.value,
    }
  }

  // new select lines from manufacturers

  getLinesManufacturersData = async (): Promise<void> => {
    this.linesManufacturersDataLoading = true
    try {
      // @TODO: Find out why this is sometimes returning a 500 status
      // Fails when doing a interchange search
      this.linesManufacturersData =
        await PartServiceProvider.getLinesManufacturers(this.partsBinId)
    } catch (_e) {
      this.linesManufacturersData = []
    }
    this.linesManufacturersDataLoading = false
  }

  setSelectedLinesManufacturers = (selectedOptions: string[]): void => {
    this.selectedLinesManufacturers = selectedOptions
    this.persistCurrentAppliedFilters()
  }

  setSelectedCatalogDiagram = (selectedCatalogDiagram: ProductModel): void => {
    this.selectedCatalogDiagram = selectedCatalogDiagram
  }

  setShowAllLinesForOrangeParts = (value: boolean): void => {
    this.showAllLinesForOrangeParts = value
  }

  setShowAddAlltoCart = (value: boolean): void => {
    this.showAddAlltoCart = value
  }

  setMultiPartInquiryResults = (
    part: ProductModel,
    activeLocation: ProductLocationModel,
    qty: number
  ): void => {
    this.multiPartInquiryResults.push({
      part,
      activeLocation,
      qty,
    })
  }

  saveSortOrderToCloud = (sortOrder: SortOrder): void => {
    const oldSortOrderString =
      StoreInstances?.userStore?.getUserAttribute(
        UserAttributeKey.displaySortOrder
      ) || ''
    let sortOrderList = oldSortOrderString.split(',')
    const sortByThenByList = sortOrder.map((order) => {
      let dir = order.dir === 'asc' ? '0' : '1'
      let property = order.property
      if (order.property === 'Recommended') {
        property = 'MCL'
        dir = '1'
      } else if (order.property === 'Availability') {
        dir = '1'
      } else if (order.property === 'Part Number') {
        property = 'PartNumber'
      } else if (order.property === 'Part Description') {
        property = 'Description'
      }
      sortOrderList = sortOrderList.filter((each) => !each.startsWith(property))
      return [property, dir].join('_')
    })
    sortOrderList.unshift(...sortByThenByList)
    const { userId } = StoreInstances.userStore.preferences
    AttributesServiceProvider.saveOrUpdateAttribute([
      {
        uuid: UuidType.DISPLAY_SORTORDER,
        domId: Number(userId),
        domType: 5,
        attrValue: sortOrderList.join(','),
      },
    ])
  }

  updateMultiPartInquiryResults = (
    part: ProductModel,
    activeLocation: ProductLocationModel,
    qty: number
  ): void => {
    const objIndex = this.multiPartInquiryResults.findIndex(
      (obj) => obj.part.lineNo === part.lineNo
    )
    this.multiPartInquiryResults[objIndex].activeLocation = activeLocation
    this.multiPartInquiryResults[objIndex].qty = qty
  }

  public findSelectedPartTypeById(partTypeId: string): PartType {
    return this.lastPartsCatalogInstance().findSelectedPartTypeById(partTypeId)
  }

  public lastPartsCatalogInstance(): PartsCatalog {
    if (this.lastNonCachedSearch === NonCachedSearchType.searchByAstNavigation)
      return StoreInstances.astPunchOutStore.catalog
    return StoreInstances.partsCatalog
  }

  resetMultiPartInquiryResults = (): void => {
    this.multiPartInquiryResults = []
  }

  handleShowAddAllToCart = (): void => {
    const shouldShow =
      this.lastNonCachedSearch === NonCachedSearchType.searchByMultiPartsInquiry
    this.setShowAddAlltoCart(shouldShow)
  }

  public setUpdatedPartInfoDetails = (
    updatedDetails: UpdatedPartInfoDetails
  ): void => {
    const hasQtyUpdatedPart = this.updatedPartInfoDetails.findIndex((item) =>
      productsMatch(updatedDetails.product, item.product)
    )
    if (hasQtyUpdatedPart > -1) {
      this.updatedPartInfoDetails[hasQtyUpdatedPart] = updatedDetails
    } else {
      this.updatedPartInfoDetails.push(updatedDetails)
    }
  }

  public setSearchResultPageScrollPosition = (position: number): void => {
    this.searchResultPageScrollPosition = position
  }

  /**
   * Method used to get results using new partsbin without changing other parts.
   * Primarily used for getting results after changing filters for AST search.
   * @param partsBinId The new partsBinId.
   */
  filterAstPartSearch = (text: string, filters: Filter[] = []): void => {
    if (this.searchResults?.partsBinId) {
      PartServiceProvider.clearPartsBinCache(this.searchResults?.partsBinId)
    }
    const reqDataBase = this.prepareCommonRequestData(0)
    const reqData = {
      ...reqDataBase,
      userContext: {
        visitorId: hawkSearchTracking.getVisitorId(),
        visitId: hawkSearchTracking.getVisitorId(),
        queryId: hawkSearchTracking.fetchOrGenerateQueryIdViaCookie(),
        'user-agent': '',
      },
      filters: filters ? filters : reqDataBase.filters,
    }

    this.lastTextSearched = text
    this.lastNonCachedSearch = NonCachedSearchType.searchByText
    PartServiceProvider.getSearchResultsByText(reqData, text, {
      catalogId: '7',
      covListId: partDomain(),
      partDomain: partDomain(),
    }).then((data) => {
      this.partsBinId = data.partsBinId?.trim() || ''

      this.searchByPartBin(0, undefined, false)
    })
  }

  private readonly persistCurrentAppliedFilters = (): void => {
    persistAppliedFilters({
      filters: this.filters,
      selectedLinesManufacturers: this.selectedLinesManufacturers,
      firstSelectedFilterType: this.firstSelectedFilterType,
    })
  }

  private readonly restoreAppliedFiltersFromSession = (): void => {
    const persisted = readPersistedAppliedFilters()
    if (!persisted) {
      this.resetFiltersForNewSearch()
      return
    }

    this.filters = persisted.filters
    this.selectedLinesManufacturers = persisted.selectedLinesManufacturers || []
    this.firstSelectedFilterType = persisted.firstSelectedFilterType
  }

  public loadResultsFromPartsbin(
    partsBinId: string,
    searchType: NonCachedSearchType,
    initialInterchangeRequest?: InterchangeRequest,
    initialInterchangeFilters?: SearchFilterModel[]
  ): void {
    this.partsBinId = partsBinId?.trim() // Trim added as part of a hack for MPV3-5658
    // this.clearGfxSvg()
    try {
      this.restoreFiltersFromCache()
    } catch (_e) {
      // Do nothing.
    }

    this.restoreAppliedFiltersFromSession()
    this.clearResults()
    this.lastNonCachedSearch = searchType
    if (
      searchType === NonCachedSearchType.searchByInterchange &&
      initialInterchangeRequest
    ) {
      this._interchangeRequestData = initialInterchangeRequest
      this.showInterchangeDisclaimers = true
      this.lastInterchangeTextSearched =
        initialInterchangeRequest.interchangeText
      if (this.filters.length > 0) {
        // Re-apply persisted facets against the restored parts bin.
        this.applyInterchangeSearchFilters()
      } else {
        this.searchByInterchange()
      }
      if (initialInterchangeFilters) {
        this.searchResults.filters = initialInterchangeFilters
      }
    } else {
      this.showInterchangeDisclaimers = false
      if (this.gfxLookupMade) {
        this.searchByGfxPartBin(0)
      } else if (this.shouldReloadTextSearchWithFilters(searchType)) {
        // AST text search applies non-stock filters by creating a new parts bin.
        // searchByPartBin alone against the unfiltered bin leaves chips on but
        // results unfiltered.
        this.lastTextSearched =
          StoreInstances.searchStore.lastTextSearched || this.lastTextSearched
        this.filterAstPartSearch(this.lastTextSearched, this.filters)
      } else {
        const isAstFilterChange =
          searchType === NonCachedSearchType.searchByAstNavigation &&
          this.filters.length > 0
        this.searchByPartBin(0, undefined, !isAstFilterChange)
      }
    }
    this.handleShowAddAllToCart()
  }

  private readonly shouldReloadTextSearchWithFilters = (
    searchType: NonCachedSearchType
  ): boolean => {
    if (searchType !== NonCachedSearchType.searchByText) return false
    return this.filters.some(
      (filter) => filter.groupName !== FilterGroupName.STOCK
    )
  }

  setSellerDetails = (value: IStoreCard): void => {
    this.sellerDetails = value
  }
}

export const useResultsStore = (): ResultStore => {
  const resultsStore = useContext(ResultsStoreContext)
  if (!resultsStore) {
    throw new Error(
      'No ResultsStoreContext.Provider found when calling useResultsStore.'
    )
  }
  return resultsStore
}
